import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import {
  ChevronLeft, Shield, Zap, Pause, Play, Settings,
  Send, Loader2, CheckCircle, AlertCircle, Copy, X,
  TrendingUp, Users, Coins, Calendar, Lock, Download, Ticket, Eye,
  ChevronDown, ClipboardCheck, Wallet, AlertTriangle,
  Gift, Hash, RefreshCw, Trophy, Search, Radio, Tv, Power,
  BarChart3, Activity, MousePointer, Gamepad2, Skull, Flame,
  Trash2, Crown, Sparkles,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatBox = ({ label, value, sub, icon: Icon, accent = 'red' }) => (
  <div className="p-4 bg-white/[0.03] border border-red-500/10 rounded-xl">
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon className={`w-3.5 h-3.5 text-${accent}-400`} />}
      <p className="text-[11px] text-white/40">{label}</p>
    </div>
    <p className={`text-xl font-bold text-${accent}-400`}>{value}</p>
    {sub && <p className="text-[10px] text-white/30 mt-0.5">{sub}</p>}
  </div>
);

const PayoutRow = ({ payout, onCopy, copied }) => (
  <div className="flex items-center justify-between py-2.5 px-3 bg-white/[0.02] rounded-lg group hover:bg-white/[0.04] transition-colors">
    <div className="flex items-center gap-3">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs
        ${payout.rank === 1 ? 'bg-yellow-500/20 text-yellow-400' : payout.rank === 2 ? 'bg-gray-400/20 text-gray-300' : payout.rank === 3 ? 'bg-amber-600/20 text-amber-500' : 'bg-white/10 text-white/40'}`}>
        #{payout.rank}
      </div>
      <div>
        <button onClick={() => onCopy(payout.wallet)} className="text-xs font-mono text-white/70 hover:text-white transition-colors flex items-center gap-1.5">
          {payout.wallet}
          {copied === payout.wallet ? <ClipboardCheck className="w-3 h-3 text-red-400" /> : <Copy className="w-3 h-3 text-white/30 opacity-0 group-hover:opacity-100" />}
        </button>
        <p className="text-[10px] text-white/30">{payout.pct}% share — {(payout.fees_stolen || 0).toLocaleString()} fees stolen</p>
      </div>
    </div>
    <div className="text-right">
      <span className="text-sm font-bold text-red-400">${(payout.amount_usd || 0).toFixed(2)}</span>
      {payout.amount_gfg > 0 && <p className="text-[10px] text-white/30">{payout.amount_gfg?.toLocaleString()} $GFG</p>}
    </div>
  </div>
);

// Top 10 Fees Stolen Leaderboard
const FeesLeaderboard = () => {
  const [weeklyData, setWeeklyData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const res = await fetch(`${API_URL}/api/weekly/info`);
        if (res.ok) setWeeklyData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchLeaderboard();
  }, []);

  const leaderboard = weeklyData?.leaderboard || [];
  const maxFees = leaderboard.length > 0 ? leaderboard[0].fees_stolen : 1;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="p-5 bg-gradient-to-b from-yellow-500/5 to-transparent border border-yellow-500/15 rounded-2xl" data-testid="fees-leaderboard">
      <div className="flex items-center gap-2 mb-1">
        <Trophy className="w-5 h-5 text-yellow-400" />
        <h2 className="font-bold text-lg">Top 10 — Fees Stolen</h2>
        {weeklyData?.launched && (
          <span className="text-[9px] px-2 py-0.5 bg-yellow-500/10 text-yellow-400 rounded-full font-semibold ml-auto">
            Week {weeklyData.current_epoch}
          </span>
        )}
      </div>
      <p className="text-[10px] text-white/25 mb-4">
        {weeklyData?.total_participants || 0} players this week // ${weeklyData?.pool_usd || 250} community rewards
      </p>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-yellow-400/50" /></div>
      ) : leaderboard.length === 0 ? (
        <p className="text-xs text-white/20 text-center py-6">No arcade games played this week yet</p>
      ) : (
        <div className="space-y-1.5">
          {leaderboard.map((p, i) => {
            const rank = p.rank || i + 1;
            const medalColor = rank === 1 ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
              rank === 2 ? 'bg-gray-400/20 text-gray-300 border-gray-400/30' :
              rank === 3 ? 'bg-amber-600/20 text-amber-400 border-amber-600/30' :
              'bg-white/5 text-white/40 border-white/10';
            const barWidth = Math.max(8, (p.fees_stolen / maxFees) * 100);
            return (
              <div key={p.wallet_address || i}
                className="flex items-center gap-3 py-2 px-3 bg-white/[0.02] hover:bg-white/[0.04] rounded-lg transition-all"
                data-testid={`fees-rank-${rank}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center font-black text-xs border flex-shrink-0 ${medalColor}`}>
                  {rank}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <code className="text-xs font-mono text-white/60 truncate">
                      {p.wallet_address?.slice(0, 8)}...{p.wallet_address?.slice(-6)}
                    </code>
                    <span className="text-[8px] text-white/20">{p.games_played} game{p.games_played !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${barWidth}%`,
                        background: rank === 1 ? 'linear-gradient(90deg, #eab308, #f59e0b)' :
                          rank === 2 ? 'linear-gradient(90deg, #9ca3af, #d1d5db)' :
                          rank === 3 ? 'linear-gradient(90deg, #d97706, #f59e0b)' :
                          'linear-gradient(90deg, rgba(239,68,68,0.4), rgba(239,68,68,0.2))'
                      }} />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className={`text-sm font-bold ${rank <= 3 ? 'text-yellow-400' : 'text-red-400'}`}>
                    {p.fees_stolen?.toLocaleString()}
                  </span>
                  <p className="text-[9px] text-white/20">fees</p>
                  {p.reward_usd > 0 && (
                    <p className="text-[8px] text-yellow-500/50">${p.reward_usd} ({p.reward_pct}%)</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};


// Metrics Dashboard
const MetricsDashboard = ({ walletAddress }) => {
  const [overview, setOverview] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEvents, setShowEvents] = useState(false);

  const fetchMetrics = useCallback(async () => {
    try {
      const [ovRes, dailyRes, funnelRes, eventsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/metrics/overview?wallet_address=${walletAddress}`),
        fetch(`${API_URL}/api/admin/metrics/daily?wallet_address=${walletAddress}&days=14`),
        fetch(`${API_URL}/api/admin/metrics/funnel?wallet_address=${walletAddress}`),
        fetch(`${API_URL}/api/admin/metrics/events?wallet_address=${walletAddress}&limit=30`),
      ]);
      if (ovRes.ok) setOverview(await ovRes.json());
      if (dailyRes.ok) { const d = await dailyRes.json(); setDailyData(d.days || []); }
      if (funnelRes.ok) { const f = await funnelRes.json(); setFunnel(f.funnel || []); }
      if (eventsRes.ok) { const e = await eventsRes.json(); setRecentEvents(e.events || []); }
    } catch (e) { console.error('Metrics fetch failed:', e); }
    finally { setLoading(false); }
  }, [walletAddress]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  if (loading) return (
    <div className="p-6 bg-white/[0.02] border border-white/[0.06] rounded-2xl flex items-center justify-center py-10">
      <Loader2 className="w-5 h-5 animate-spin text-red-400" />
    </div>
  );

  const todayDelta = (field) => {
    const t = overview?.today?.[field] || 0;
    const y = overview?.yesterday?.[field] || 0;
    if (y === 0) return t > 0 ? '+100%' : '—';
    const pct = Math.round(((t - y) / y) * 100);
    return pct >= 0 ? `+${pct}%` : `${pct}%`;
  };

  const maxBarVal = Math.max(...dailyData.map(d => d.active_wallets || 0), 1);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
      className="mb-8" data-testid="metrics-dashboard">

      {/* Metric Cards */}
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-4 h-4 text-red-400" />
        <h2 className="font-bold text-sm tracking-wide">Live Metrics</h2>
        <button onClick={fetchMetrics} className="ml-auto text-white/30 hover:text-red-400 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Total Wallets', value: overview?.total_profiles || 0, icon: Users, delta: null },
          { label: 'Active Today', value: overview?.today?.active_wallets || 0, icon: Activity, delta: todayDelta('active_wallets') },
          { label: 'Games Today', value: overview?.today?.games_played || 0, icon: Gamepad2, delta: todayDelta('games_played') },
          { label: 'Page Views Today', value: overview?.today?.page_views || 0, icon: MousePointer, delta: todayDelta('page_views') },
        ].map((m) => (
          <div key={m.label} className="p-3 bg-white/[0.03] border border-red-500/10 rounded-xl">
            <div className="flex items-center gap-1.5 mb-1">
              <m.icon className="w-3 h-3 text-red-400/70" />
              <span className="text-[10px] text-white/40">{m.label}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-bold text-red-400">{m.value.toLocaleString()}</span>
              {m.delta && (
                <span className={`text-[10px] font-semibold ${m.delta.startsWith('+') ? 'text-emerald-400' : m.delta.startsWith('-') ? 'text-red-500' : 'text-white/30'}`}>
                  {m.delta}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Activity Chart (last 14 days) */}
      {dailyData.length > 0 && (
        <div className="p-4 bg-white/[0.02] border border-white/[0.06] rounded-xl mb-5">
          <p className="text-[10px] text-white/40 uppercase tracking-wider mb-3">Daily Active Wallets (14d)</p>
          <div className="flex items-end gap-1 h-20">
            {dailyData.map((d, i) => {
              const val = d.active_wallets || 0;
              const h = Math.max((val / maxBarVal) * 100, 4);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/90 border border-white/10 rounded px-1.5 py-0.5 text-[9px] text-white whitespace-nowrap z-10">
                    {d.date?.slice(5)} — {val}
                  </div>
                  <div
                    className="w-full rounded-t transition-all duration-300 group-hover:opacity-80"
                    style={{
                      height: `${h}%`,
                      background: i === dailyData.length - 1
                        ? 'linear-gradient(180deg, #ef4444, #dc2626)'
                        : 'linear-gradient(180deg, rgba(239,68,68,0.4), rgba(239,68,68,0.15))',
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Conversion Funnel */}
      {funnel.length > 0 && (
        <div className="p-4 bg-white/[0.02] border border-white/[0.06] rounded-xl mb-5">
          <p className="text-[10px] text-white/40 uppercase tracking-wider mb-3">Conversion Funnel</p>
          <div className="space-y-2">
            {funnel.map((step, i) => {
              const maxCount = funnel[0]?.count || 1;
              const pct = maxCount > 0 ? Math.round((step.count / maxCount) * 100) : 0;
              return (
                <div key={i}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-white/60">{step.stage}</span>
                    <span className="text-red-400 font-semibold">{step.count}</span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #ef4444, #f97316)' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Events */}
      <div className="p-4 bg-white/[0.02] border border-white/[0.06] rounded-xl">
        <button onClick={() => setShowEvents(!showEvents)} className="w-full flex items-center justify-between" data-testid="events-feed-toggle">
          <div className="flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-red-400" />
            <span className="text-xs font-medium">Recent Events</span>
            <span className="text-[10px] text-white/30">{recentEvents.length}</span>
          </div>
          <ChevronDown className={`w-3.5 h-3.5 text-white/40 transition-transform ${showEvents ? 'rotate-180' : ''}`} />
        </button>
        <AnimatePresence>
          {showEvents && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="mt-3 space-y-1 max-h-48 overflow-y-auto">
                {recentEvents.length > 0 ? recentEvents.map((ev, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 px-2 bg-white/[0.02] rounded text-[10px]">
                    <div className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 rounded font-semibold ${
                        ev.event_type === 'wallet_connect' ? 'bg-emerald-500/15 text-emerald-400' :
                        ev.event_type === 'page_view' ? 'bg-blue-500/15 text-blue-400' :
                        'bg-red-500/15 text-red-400'
                      }`}>{ev.event_type}</span>
                      {ev.page && <span className="text-white/40">{ev.page}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {ev.wallet_address && <span className="text-white/20 font-mono">{ev.wallet_address.slice(0, 4)}..{ev.wallet_address.slice(-3)}</span>}
                      <span className="text-white/20">{ev.timestamp?.slice(11, 16)}</span>
                    </div>
                  </div>
                )) : (
                  <p className="text-[10px] text-white/30 text-center py-3">No events yet — they'll appear as users interact</p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};



// Feature Access Control Panel
const FeatureAccessControl = ({ adminToken, walletAddress }) => {
  const [features, setFeatures] = useState({ arcade: false, airdrop: false, oracle: false, graveyard: false });
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  const fetchFeatures = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/features/status`);
      if (res.ok) { const d = await res.json(); setFeatures(d.features || {}); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchFeatures(); }, [fetchFeatures]);

  const toggleFeature = async (feature, enabled) => {
    setToggling(feature);
    try {
      const res = await fetch(`${API_URL}/api/admin/features/toggle?feature=${feature}&enabled=${enabled}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
      });
      if (res.ok) {
        setFeatures(prev => ({ ...prev, [feature]: enabled }));
      } else {
        const err = await res.json();
        alert(err.detail || 'Toggle failed');
      }
    } catch { alert('Request failed'); }
    finally { setToggling(null); }
  };

  const featureConfig = [
    { key: 'arcade', label: 'Arcade', desc: 'Fee Scrambler, Whack, Safe Cracker, Fee Heist', icon: Gamepad2, color: 'purple' },
    { key: 'airdrop', label: 'Airdrop', desc: 'Task registration & points accumulation', icon: Gift, color: 'emerald' },
    { key: 'oracle', label: 'Price Oracle', desc: 'Prediction game & cosmetics earning', icon: Eye, color: 'cyan' },
    { key: 'graveyard', label: 'Graveyard', desc: 'Failed TX submission for points', icon: Skull, color: 'red' },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="p-5 bg-white/[0.02] border border-red-500/20 rounded-2xl mb-6" data-testid="feature-access-control">
      <div className="flex items-center gap-2 mb-4">
        <Lock className="w-4 h-4 text-red-400" />
        <h2 className="font-bold text-sm">Feature Access Control</h2>
        {!adminToken && <span className="ml-auto text-[8px] px-2 py-0.5 bg-yellow-500/10 text-yellow-400 rounded-full font-bold">SIGN TO UNLOCK</span>}
      </div>

      {!adminToken ? (
        <p className="text-xs text-white/30 text-center py-4">Sign with your wallet to unlock feature toggles</p>
      ) : loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-red-400" /></div>
      ) : (
        <div className="space-y-2">
          {featureConfig.map(f => {
            const enabled = features[f.key] || false;
            const isToggling = toggling === f.key;
            return (
              <div key={f.key}
                className={`flex items-center justify-between p-3 rounded-xl transition-all ${enabled ? `bg-${f.color}-500/5 border border-${f.color}-500/15` : 'bg-white/[0.02] border border-white/[0.06]'}`}
                data-testid={`feature-toggle-${f.key}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${enabled ? `bg-${f.color}-500/15` : 'bg-white/5'}`}>
                    <f.icon className={`w-4 h-4 ${enabled ? `text-${f.color}-400` : 'text-white/30'}`} />
                  </div>
                  <div>
                    <p className={`text-xs font-semibold ${enabled ? 'text-white' : 'text-white/50'}`}>{f.label}</p>
                    <p className="text-[9px] text-white/25">{f.desc}</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleFeature(f.key, !enabled)}
                  disabled={isToggling}
                  className={`relative w-11 h-6 rounded-full transition-all duration-300 ${enabled ? 'bg-emerald-500' : 'bg-white/10'}`}
                  data-testid={`toggle-${f.key}-switch`}>
                  {isToggling ? (
                    <Loader2 className="w-3 h-3 animate-spin text-white absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                  ) : (
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${enabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};


// Season Management Control
const SeasonControl = ({ adminToken }) => {
  const [season, setSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [ending, setEnding] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [endResult, setEndResult] = useState(null);
  const [startingDemo, setStartingDemo] = useState(false);
  const [form, setForm] = useState({ name: '', theme: '', burn_target: 5000000, prize_pool: 15000000, duration_days: 30 });

  const fetchSeason = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/seasons/current`);
      const data = await res.json();
      setSeason(data.season);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchSeason(); }, [fetchSeason]);

  const createSeason = async () => {
    if (!adminToken || !form.name) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/seasons/create`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) { setShowCreate(false); fetchSeason(); }
    } catch {}
    setCreating(false);
  };

  const startDemoSeason = async () => {
    if (!adminToken || startingDemo) return;
    setStartingDemo(true);
    try {
      const preset = {
        name: 'Demo Descent',
        theme: 'Pre-launch showcase — the community warms up before $GFG goes live.',
        burn_target: 100000,
        prize_pool: 1000000,
        duration_days: 30,
        is_demo: true,
      };
      const res = await fetch(`${API_URL}/api/admin/seasons/create`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(preset),
      });
      const data = await res.json();
      if (data.success) {
        // Note: do NOT toggle `burn_portal_enabled` here — that is a GLOBAL flag
        // that would expose the portal to real (non-demo) wallets before $GFG launches.
        // Demo wallets already pass the `isDemo` gate on SeasonsPage.js.
        fetchSeason();
      }
    } catch {}
    setStartingDemo(false);
  };

  const endSeason = async () => {
    if (!adminToken) return;
    setEnding(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/seasons/end`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
      });
      const data = await res.json();
      setEndResult(data);
      fetchSeason();
    } catch {}
    setEnding(false);
  };

  const toggleBurnPortal = async () => {
    if (!adminToken) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/seasons/toggle-burn-portal`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
      });
      const data = await res.json();
      if (data.success) fetchSeason();
    } catch {}
  };

  if (loading) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="p-5 bg-white/[0.02] border border-orange-500/20 rounded-2xl" data-testid="season-control">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-400" />
          <h3 className="font-semibold text-sm">Season Control</h3>
        </div>
        {!season && (
          <button onClick={() => setShowCreate(!showCreate)}
            className="text-[10px] font-mono px-3 py-1 rounded-sm" data-testid="create-season-btn"
            style={{ background: 'rgba(255,107,0,0.1)', border: '1px solid rgba(255,107,0,0.2)', color: '#ff6b00' }}>
            {showCreate ? 'CANCEL' : 'NEW SEASON'}
          </button>
        )}
      </div>

      {season ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-mono font-bold" style={{ color: '#ff6b00' }}>{season.name}</p>
              <p className="text-[9px] font-mono text-white/30">{season.theme}</p>
            </div>
            <span className="text-[9px] font-mono px-2 py-0.5 rounded-sm" style={{ background: 'rgba(57,255,20,0.08)', color: '#39ff14', border: '1px solid rgba(57,255,20,0.15)' }}>ACTIVE</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 rounded-md" style={{ background: 'rgba(255,255,255,0.02)' }}>
              <p className="text-xs font-mono font-bold" style={{ color: '#ff6b00' }}>{season.burn_progress || 0}%</p>
              <p className="text-[8px] text-white/20">Burn Progress</p>
            </div>
            <div className="p-2 rounded-md" style={{ background: 'rgba(255,255,255,0.02)' }}>
              <p className="text-xs font-mono font-bold text-white/60">{season.total_participants || 0}</p>
              <p className="text-[8px] text-white/20">Players</p>
            </div>
            <div className="p-2 rounded-md" style={{ background: 'rgba(255,255,255,0.02)' }}>
              <p className="text-xs font-mono font-bold text-white/60">{(season.total_burned || 0).toLocaleString()}</p>
              <p className="text-[8px] text-white/20">Burned</p>
            </div>
          </div>
          <button onClick={toggleBurnPortal}
            data-testid="toggle-burn-portal-btn"
            className="w-full mt-2 py-2 text-[10px] font-mono tracking-wider rounded-sm transition-all"
            style={{
              background: season.burn_portal_enabled ? 'rgba(57,255,20,0.06)' : 'rgba(255,107,0,0.06)',
              border: `1px solid ${season.burn_portal_enabled ? 'rgba(57,255,20,0.15)' : 'rgba(255,107,0,0.15)'}`,
              color: season.burn_portal_enabled ? '#39ff14' : '#ff6b00',
            }}>
            BURN PORTAL: {season.burn_portal_enabled ? 'ON' : 'OFF'}
          </button>
          <button onClick={endSeason} disabled={ending}
            data-testid="end-season-btn"
            className="w-full mt-2 py-2 text-[10px] font-mono tracking-wider rounded-sm transition-all"
            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444' }}>
            {ending ? 'ENDING...' : 'END SEASON & DISTRIBUTE'}
          </button>
          {endResult && (
            <div className="mt-2 p-3 rounded-md text-[10px] font-mono" style={{ background: endResult.burn_target_met ? 'rgba(57,255,20,0.05)' : 'rgba(239,68,68,0.05)', border: `1px solid ${endResult.burn_target_met ? 'rgba(57,255,20,0.15)' : 'rgba(239,68,68,0.15)'}` }}>
              <p style={{ color: endResult.burn_target_met ? '#39ff14' : '#ef4444' }}>
                {endResult.burn_target_met ? 'TARGET MET // Rewards distributed!' : 'TARGET MISSED // Rewards roll over'}
              </p>
              <p className="text-white/30 mt-1">{endResult.total_participants} participants // {(endResult.total_burned || 0).toLocaleString()} burned</p>
            </div>
          )}
        </div>
      ) : showCreate ? (
        <div className="space-y-3">
          <input placeholder="Season Name (e.g. The Flood)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 text-xs font-mono rounded-sm outline-none"
            data-testid="season-name-input"
            style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,107,0,0.1)', color: '#ff6b00' }} />
          <input placeholder="Theme description" value={form.theme} onChange={e => setForm({ ...form, theme: e.target.value })}
            className="w-full px-3 py-2 text-xs font-mono rounded-sm outline-none"
            style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)' }} />
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[8px] font-mono text-white/20">Burn Target</label>
              <input type="number" value={form.burn_target} onChange={e => setForm({ ...form, burn_target: parseInt(e.target.value) || 0 })}
                className="w-full px-2 py-1.5 text-xs font-mono rounded-sm outline-none"
                style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)' }} />
            </div>
            <div>
              <label className="text-[8px] font-mono text-white/20">Community Rewards</label>
              <input type="number" value={form.prize_pool} onChange={e => setForm({ ...form, prize_pool: parseInt(e.target.value) || 0 })}
                className="w-full px-2 py-1.5 text-xs font-mono rounded-sm outline-none"
                style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)' }} />
            </div>
            <div>
              <label className="text-[8px] font-mono text-white/20">Duration (days)</label>
              <input type="number" value={form.duration_days} onChange={e => setForm({ ...form, duration_days: parseInt(e.target.value) || 30 })}
                className="w-full px-2 py-1.5 text-xs font-mono rounded-sm outline-none"
                style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)' }} />
            </div>
          </div>
          <button onClick={createSeason} disabled={creating || !form.name}
            data-testid="submit-season-btn"
            className="w-full py-2 text-[10px] font-mono tracking-wider rounded-sm transition-all"
            style={{ background: 'rgba(255,107,0,0.1)', border: '1px solid rgba(255,107,0,0.25)', color: '#ff6b00', opacity: !form.name ? 0.3 : 1 }}>
            {creating ? 'CREATING...' : 'LAUNCH SEASON'}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <button
            onClick={startDemoSeason}
            disabled={startingDemo || !adminToken}
            data-testid="start-demo-season-btn"
            className="w-full py-3 text-[11px] font-mono tracking-[0.2em] uppercase rounded-sm transition-all flex items-center justify-center gap-2 disabled:opacity-40"
            style={{
              background: 'linear-gradient(180deg, rgba(255,107,0,0.18), rgba(124,30,0,0.3))',
              border: '1px solid rgba(255,149,0,0.4)',
              color: '#ffd700',
              boxShadow: '0 0 20px rgba(255,107,0,0.15)',
            }}
          >
            <Flame className="w-3.5 h-3.5" />
            {startingDemo ? 'Igniting...' : 'Start Demo Season (1-click)'}
          </button>
          <p className="text-[9px] font-mono text-white/30 text-center leading-relaxed">
            Presets: <span className="text-orange-300/70">100K target · 1M rewards · 30 days · burn portal ON</span>.
            Perfect for judge walkthroughs — wipe demo data before real launch.
          </p>
          <div className="flex items-center gap-2 my-1">
            <div className="flex-1 h-[1px] bg-white/5" />
            <span className="text-[8px] font-mono text-white/20 tracking-wider">OR CUSTOM</span>
            <div className="flex-1 h-[1px] bg-white/5" />
          </div>
          <p className="text-[10px] font-mono text-white/20 text-center">
            Click <span className="text-orange-300/60">NEW SEASON</span> above to configure manually.
          </p>
        </div>
      )}
    </motion.div>
  );
};

// Airdrop Phase Control
const AirdropPhaseControl = ({ walletAddress, adminToken }) => {
  const [airdropState, setAirdropState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [result, setResult] = useState(null);
  const [csvLoading, setCsvLoading] = useState(null);

  const downloadCSV = async (roundNum) => {
    if (!adminToken) { alert('Please sign with your wallet first to unlock admin controls.'); return; }
    setCsvLoading(roundNum);
    try {
      const res = await fetch(`${API_URL}/api/admin/airdrop/export-csv?round_num=${roundNum}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` },
      });
      if (!res.ok) {
        const err = await res.json();
        setResult({ success: false, message: err.detail || 'CSV export failed' });
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gfg_airdrop_round_${roundNum}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setResult({ success: true, message: `Round ${roundNum} CSV downloaded!` });
    } catch (e) { setResult({ success: false, message: 'CSV download failed' }); }
    finally { setCsvLoading(null); setTimeout(() => setResult(null), 5000); }
  };

  const fetchState = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/airdrop/info`);
      if (res.ok) setAirdropState(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchState(); }, [fetchState]);

  const doAction = async (endpoint, label) => {
    if (!adminToken) { alert('Please sign with your wallet first to unlock admin controls.'); return; }
    if (!window.confirm(`Are you sure you want to: ${label}?`)) return;
    setActionLoading(label);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/airdrop/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
      });
      const data = await res.json();
      setResult({ success: data.success, message: data.message || data.detail });
      if (data.success) fetchState();
    } catch (e) { setResult({ success: false, message: 'Request failed' }); }
    finally { setActionLoading(null); setTimeout(() => setResult(null), 5000); }
  };

  const distributeRound = async (roundNum) => {
    if (!adminToken) { alert('Please sign with your wallet first to unlock admin controls.'); return; }
    if (!window.confirm(`Distribute Round ${roundNum}? This will send tokens to all qualified wallets.`)) return;
    setActionLoading(`round-${roundNum}`);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/airdrop/distribute?round_num=${roundNum}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
      });
      const data = await res.json();
      setResult({ success: data.success, message: data.message || data.detail });
      if (data.success) fetchState();
    } catch (e) { setResult({ success: false, message: 'Distribution failed' }); }
    finally { setActionLoading(null); setTimeout(() => setResult(null), 5000); }
  };

  const status = airdropState?.status || 'inactive';
  const completedRounds = (airdropState?.rounds_completed || []).map(r => r.round);
  const roundAllocations = airdropState?.round_allocations || [15000000, 15000000, 20000000];

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="p-5 bg-gradient-to-b from-emerald-500/5 to-transparent border border-emerald-500/20 rounded-2xl mb-6" data-testid="airdrop-phase-control">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-emerald-400" />
          <h2 className="font-bold text-lg">Airdrop Control</h2>
          {!adminToken && <span className="ml-1 text-[8px] px-2 py-0.5 bg-yellow-500/10 text-yellow-400 rounded-full font-bold">SIGN TO UNLOCK</span>}
        </div>
        <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${
          status === 'inactive' ? 'bg-red-500/20 text-red-400' :
          status === 'accumulating' ? 'bg-emerald-500/20 text-emerald-400' :
          status === 'distributing' ? 'bg-yellow-500/20 text-yellow-400' :
          'bg-blue-500/20 text-blue-400'
        }`}>
          {status.toUpperCase()}
        </span>
      </div>

      {/* Stats */}
      {!loading && (
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="p-2.5 bg-white/[0.03] rounded-lg">
            <p className="text-[9px] text-white/30">Participants</p>
            <p className="text-sm font-bold text-emerald-400">{airdropState?.total_participants || 0}</p>
          </div>
          <div className="p-2.5 bg-white/[0.03] rounded-lg">
            <p className="text-[9px] text-white/30">Qualified</p>
            <p className="text-sm font-bold text-emerald-400">{airdropState?.qualified_participants || 0}</p>
          </div>
        </div>
      )}

      {/* Activate / Cancel */}
      <div className="flex gap-2 mb-4">
        {status === 'inactive' ? (
          <button onClick={() => doAction('activate', 'Activate Airdrop')}
            disabled={!!actionLoading}
            className="flex-1 py-2.5 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 rounded-lg text-xs font-semibold text-emerald-400 transition-all flex items-center justify-center gap-1.5"
            data-testid="activate-airdrop-btn">
            {actionLoading === 'Activate Airdrop' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            Activate Airdrop
          </button>
        ) : (
          <button onClick={() => doAction('cancel', 'Cancel Airdrop')}
            disabled={!!actionLoading}
            className="flex-1 py-2.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg text-xs font-semibold text-red-400 transition-all flex items-center justify-center gap-1.5"
            data-testid="cancel-airdrop-btn">
            {actionLoading === 'Cancel Airdrop' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pause className="w-3.5 h-3.5" />}
            Pause Airdrop
          </button>
        )}
      </div>

      {/* Distribution Rounds */}
      <p className="text-[9px] text-white/30 uppercase tracking-wider mb-2">Distribution Rounds</p>
      <div className="space-y-2 mb-4">
        {[1, 2, 3].map(round => {
          const isCompleted = completedRounds.includes(round);
          const tokens = roundAllocations[round - 1] || 0;
          return (
            <div key={round} className={`flex items-center justify-between px-3 py-2.5 rounded-lg ${isCompleted ? 'bg-emerald-500/5 border border-emerald-500/15' : 'bg-white/[0.02] border border-white/[0.06]'}`}
              data-testid={`airdrop-round-${round}`}>
              <div>
                <p className={`text-xs font-semibold ${isCompleted ? 'text-emerald-400' : 'text-white/50'}`}>
                  Round {round}
                </p>
                <p className="text-[9px] text-white/25">{(tokens / 1000000).toFixed(0)}M $GFG</p>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => downloadCSV(round)}
                  disabled={!!csvLoading}
                  className="px-2.5 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 rounded-lg text-[10px] font-semibold text-white/50 hover:text-white/80 transition-all disabled:opacity-30 flex items-center gap-1"
                  data-testid={`export-csv-round-${round}-btn`}
                  title={`Download Round ${round} allocations as CSV`}>
                  {csvLoading === round ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                  CSV
                </button>
                {isCompleted ? (
                  <span className="flex items-center gap-1 text-[9px] font-bold text-emerald-400">
                    <CheckCircle className="w-3 h-3" /> Done
                  </span>
                ) : (
                  <button onClick={() => distributeRound(round)}
                    disabled={!!actionLoading || status === 'inactive'}
                    className="px-3 py-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/20 rounded-lg text-[10px] font-semibold text-emerald-400 transition-all disabled:opacity-30"
                    data-testid={`distribute-round-${round}-btn`}>
                    {actionLoading === `round-${round}` ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Distribute'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Result Message */}
      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className={`p-3 rounded-lg text-xs flex items-center gap-2 ${result.success ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
            {result.success ? <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />}
            {result.message}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};


// Airdrop Codes Management Section
const AirdropCodesSection = ({ walletAddress }) => {
  const [codes, setCodes] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [participantStats, setParticipantStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [showCodes, setShowCodes] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [walletSearch, setWalletSearch] = useState('');
  const [codeFilter, setCodeFilter] = useState('all');
  const [codeCopied, setCodeCopied] = useState(null);
  const [genCount, setGenCount] = useState(10);
  const [genSingleUse, setGenSingleUse] = useState(false);

  const fetchCodes = useCallback(async () => {
    if (!walletAddress) return;
    setLoading(true);
    try {
      const [codesRes, participantsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/airdrop/codes?wallet_address=${walletAddress}`),
        fetch(`${API_URL}/api/admin/airdrop/participants?wallet_address=${walletAddress}`),
      ]);
      if (codesRes.ok) {
        const data = await codesRes.json();
        setCodes(data.codes || []);
      }
      if (participantsRes.ok) {
        const data = await participantsRes.json();
        setParticipants(data.participants || []);
        setParticipantStats({ total: data.total, qualified: data.qualified, total_points: data.total_points });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [walletAddress]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const generateCodes = async (taskType) => {
    if (generating) return;
    setGenerating(true);
    setGenResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/airdrop/generate-codes?wallet_address=${walletAddress}&task_type=${taskType}&count=${genCount}&single_use=${genSingleUse}`, { method: 'POST' });
      const data = await res.json();
      setGenResult(data);
      if (data.success) fetchCodes();
    } catch { setGenResult({ success: false, message: 'Failed to generate codes' }); }
    finally { setGenerating(false); setTimeout(() => setGenResult(null), 5000); }
  };

  const discordCodes = codes.filter(c => c.task_type === 'discord_code');
  const twitterCodes = codes.filter(c => c.task_type === 'twitter_code');
  const filteredCodes = codeFilter === 'all' ? codes : codeFilter === 'discord' ? discordCodes : twitterCodes;
  const availableCodes = filteredCodes.filter(c => !c.used && !c.expired);

  return (
    <div className="space-y-4">
      {/* Airdrop Stats */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="p-5 bg-gradient-to-b from-red-500/5 to-transparent border border-red-500/20 rounded-2xl" data-testid="airdrop-admin-section">
        <div className="flex items-center gap-2 mb-4">
          <Gift className="w-5 h-5 text-red-400" />
          <h2 className="font-bold text-lg">Airdrop Management</h2>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="p-3 bg-red-500/5 border border-red-500/10 rounded-xl text-center">
            <p className="text-lg font-bold text-red-400">{participantStats.total || 0}</p>
            <p className="text-[9px] text-white/30">Participants</p>
          </div>
          <div className="p-3 bg-red-500/5 border border-red-500/10 rounded-xl text-center">
            <p className="text-lg font-bold text-red-300">{participantStats.qualified || 0}</p>
            <p className="text-[9px] text-white/30">Qualified</p>
          </div>
          <div className="p-3 bg-red-500/5 border border-red-500/10 rounded-xl text-center">
            <p className="text-lg font-bold text-red-300">{codes.length}</p>
            <p className="text-[9px] text-white/30">Total Codes</p>
          </div>
        </div>

        {/* Generate Controls */}
        <div className="mb-4 p-4 bg-white/[0.02] border border-white/[0.05] rounded-xl">
          <p className="text-xs text-red-400/60 font-semibold uppercase tracking-wider mb-3">Generate Codes</p>
          <div className="flex items-center gap-2 mb-3">
            <label className="text-xs text-white/40">Count:</label>
            <input type="number" value={genCount} onChange={e => setGenCount(Math.min(200, Math.max(1, parseInt(e.target.value) || 1)))}
              className="w-16 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-red-500/50"
              data-testid="gen-count-input" />
            <label className="flex items-center gap-1.5 text-xs text-white/40 ml-2 cursor-pointer">
              <input type="checkbox" checked={genSingleUse} onChange={e => setGenSingleUse(e.target.checked)}
                className="w-3.5 h-3.5 rounded accent-red-500" />
              Single-use
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => generateCodes('discord_code')} disabled={generating}
              className="py-2.5 bg-indigo-500/20 hover:bg-indigo-500/30 border border-indigo-500/30 rounded-lg font-semibold text-xs text-indigo-300 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="gen-discord-codes-btn">
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Hash className="w-3.5 h-3.5" />}
              Discord Codes
            </button>
            <button onClick={() => generateCodes('twitter_code')} disabled={generating}
              className="py-2.5 bg-sky-500/20 hover:bg-sky-500/30 border border-sky-500/30 rounded-lg font-semibold text-xs text-sky-300 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="gen-twitter-codes-btn">
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Hash className="w-3.5 h-3.5" />}
              Twitter Codes
            </button>
          </div>
          <AnimatePresence>
            {genResult && (
              <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className={`mt-3 px-3 py-2 rounded-lg text-xs flex items-center gap-2 ${genResult.success ? 'bg-red-500/10 text-red-300' : 'bg-orange-500/10 text-orange-400'}`}>
                {genResult.success ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                {genResult.message}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Code Breakdown */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="p-2.5 bg-indigo-500/5 border border-indigo-500/10 rounded-lg text-center">
            <p className="text-sm font-bold text-indigo-300">{discordCodes.length}</p>
            <p className="text-[9px] text-white/30">Discord (SEWER-)</p>
            <p className="text-[9px] text-indigo-400/60">{discordCodes.filter(c => !c.used).length} available</p>
          </div>
          <div className="p-2.5 bg-sky-500/5 border border-sky-500/10 rounded-lg text-center">
            <p className="text-sm font-bold text-sky-300">{twitterCodes.length}</p>
            <p className="text-[9px] text-white/30">Twitter (TWEET-)</p>
            <p className="text-[9px] text-sky-400/60">{twitterCodes.filter(c => !c.used).length} available</p>
          </div>
        </div>

        {/* Toggle code list */}
        <button onClick={() => setShowCodes(!showCodes)}
          className="w-full py-2 bg-white/[0.03] hover:bg-white/[0.05] border border-white/[0.06] rounded-lg text-xs text-white/50 hover:text-white/70 transition-all flex items-center justify-center gap-1 mb-2"
          data-testid="toggle-airdrop-codes-btn">
          <ChevronDown className={`w-3 h-3 transition-transform ${showCodes ? 'rotate-180' : ''}`} />
          {showCodes ? 'Hide Codes' : `View All Codes (${codes.length})`}
        </button>

        <AnimatePresence>
          {showCodes && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              {/* Filter tabs */}
              <div className="flex gap-1 mb-2">
                {['all', 'discord', 'twitter'].map(f => (
                  <button key={f} onClick={() => setCodeFilter(f)}
                    className={`px-3 py-1 rounded-lg text-[10px] font-semibold uppercase tracking-wider transition-all ${codeFilter === f ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-white/[0.03] text-white/30 border border-white/[0.05]'}`}>
                    {f} ({f === 'all' ? codes.length : f === 'discord' ? discordCodes.length : twitterCodes.length})
                  </button>
                ))}
              </div>

              {/* Copy all available */}
              <button onClick={() => {
                const available = availableCodes.map(c => c.code).join('\n');
                navigator.clipboard.writeText(available);
                setCodeCopied('all');
                setTimeout(() => setCodeCopied(null), 2000);
              }}
                className="w-full py-2 mb-2 bg-red-500/10 hover:bg-red-500/15 border border-red-500/20 rounded-lg text-xs text-red-400 font-medium transition-all flex items-center justify-center gap-1"
                data-testid="copy-all-airdrop-codes-btn">
                {codeCopied === 'all' ? <><CheckCircle className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy Available ({availableCodes.length})</>}
              </button>

              {/* Code list */}
              <div className="max-h-64 overflow-y-auto space-y-1 pr-1 scrollbar-thin" data-testid="airdrop-codes-list">
                {filteredCodes.map(c => (
                  <div key={c.code}
                    className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs ${c.used ? 'bg-red-500/5 border border-red-500/10' : 'bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.04]'} transition-all`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.used ? 'bg-red-400' : 'bg-green-400'}`} />
                      <code className="font-mono font-semibold text-white/70">{c.code}</code>
                      <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${c.task_type === 'discord_code' ? 'bg-indigo-500/10 text-indigo-400' : 'bg-sky-500/10 text-sky-400'}`}>
                        {c.task_type === 'discord_code' ? 'Discord' : 'Twitter'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {c.use_count > 0 && (
                        <span className="text-[9px] text-white/25">{c.use_count} use{c.use_count !== 1 ? 's' : ''}</span>
                      )}
                      {!c.used && (
                        <button onClick={() => {
                          navigator.clipboard.writeText(c.code);
                          setCodeCopied(c.code);
                          setTimeout(() => setCodeCopied(null), 1500);
                        }} className="p-1 hover:bg-white/10 rounded transition-colors">
                          {codeCopied === c.code ? <CheckCircle className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3 text-white/30" />}
                        </button>
                      )}
                      <span className={`text-[9px] font-semibold ${c.used ? 'text-red-400' : 'text-green-400'}`}>
                        {c.used ? 'USED' : 'OPEN'}
                      </span>
                    </div>
                  </div>
                ))}
                {filteredCodes.length === 0 && (
                  <p className="text-xs text-white/20 text-center py-4">No codes generated yet</p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Participants Section */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="p-5 bg-white/[0.02] border border-red-500/10 rounded-2xl" data-testid="airdrop-participants-section">
        <button onClick={() => setShowParticipants(!showParticipants)} className="w-full flex items-center justify-between"
          data-testid="toggle-participants-btn">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-red-400" />
            <span className="text-sm font-medium">Airdrop Participants ({participants.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={(e) => { e.stopPropagation(); fetchCodes(); }} className="p-1 hover:bg-white/10 rounded transition-colors" data-testid="refresh-participants-btn">
              <RefreshCw className={`w-3.5 h-3.5 text-white/30 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${showParticipants ? 'rotate-180' : ''}`} />
          </div>
        </button>
        <AnimatePresence>
          {showParticipants && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              {/* Search Bar */}
              <div className="mt-3 mb-3 relative" data-testid="wallet-search-container">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/20" />
                <input
                  type="text"
                  value={walletSearch}
                  onChange={e => setWalletSearch(e.target.value)}
                  placeholder="Search by wallet address, task, or status..."
                  className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg pl-9 pr-8 py-2 text-xs text-white placeholder-white/20 focus:outline-none focus:border-red-500/40 transition-colors"
                  data-testid="wallet-search-input"
                />
                {walletSearch && (
                  <button onClick={() => setWalletSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/50 transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="max-h-96 overflow-y-auto space-y-2 scrollbar-thin">
                {participants
                  .filter(p => {
                    if (!walletSearch.trim()) return true;
                    const q = walletSearch.toLowerCase().trim();
                    const addr = (p.wallet_address || '').toLowerCase();
                    const tasks = (p.completed_tasks || []).map(t => t.task).join(' ').toLowerCase();
                    const qualified = p.total_points >= 200 ? 'qualified' : 'unqualified';
                    return addr.includes(q) || tasks.includes(q) || qualified.includes(q);
                  })
                  .map((p, i) => {
                  const isQualified = p.total_points >= 200;
                  const tasks = p.completed_tasks || [];
                  return (
                    <div key={p.wallet_address || i}
                      className={`p-3 rounded-xl transition-all ${isQualified ? 'bg-red-500/5 border border-red-500/10' : 'bg-white/[0.02] border border-white/[0.05]'}`}
                      data-testid={`participant-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <code className="text-xs font-mono text-white/60">
                            {p.wallet_address?.slice(0, 6)}...{p.wallet_address?.slice(-4)}
                          </code>
                          {isQualified && (
                            <span className="text-[8px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded-full font-bold">QUALIFIED</span>
                          )}
                        </div>
                        <span className="text-sm font-bold text-red-400">{p.total_points} pts</span>
                      </div>
                      {/* Completed tasks */}
                      <div className="flex flex-wrap gap-1">
                        {tasks.map((t, ti) => (
                          <span key={ti} className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium
                            ${t.task === 'connect_wallet' ? 'bg-white/5 text-white/40' :
                              t.task === 'discord_code' ? 'bg-indigo-500/10 text-indigo-400' :
                              t.task === 'twitter_code' ? 'bg-sky-500/10 text-sky-400' :
                              t.task === 'arcade_play' ? 'bg-purple-500/10 text-purple-400' :
                              t.task === 'graveyard_submit' ? 'bg-red-500/10 text-red-400' :
                              t.task === 'referral' ? 'bg-yellow-500/10 text-yellow-400' :
                              'bg-white/5 text-white/30'}`}>
                            {t.task === 'connect_wallet' ? 'Wallet' :
                             t.task === 'discord_code' ? 'Discord' :
                             t.task === 'twitter_code' ? 'Twitter' :
                             t.task === 'arcade_play' ? 'Arcade' :
                             t.task === 'graveyard_submit' ? 'Graveyard' :
                             t.task === 'referral' ? `Referral` :
                             t.task} +{t.points}
                          </span>
                        ))}
                      </div>
                      {p.referral_count > 0 && (
                        <p className="text-[9px] text-yellow-400/60 mt-1">{p.referral_count} referral{p.referral_count !== 1 ? 's' : ''}</p>
                      )}
                    </div>
                  );
                })}
                {participants.length === 0 && (
                  <p className="text-xs text-white/20 text-center py-4">No participants yet</p>
                )}
                {walletSearch && participants.length > 0 && participants.filter(p => {
                  const q = walletSearch.toLowerCase().trim();
                  const addr = (p.wallet_address || '').toLowerCase();
                  const tasks = (p.completed_tasks || []).map(t => t.task).join(' ').toLowerCase();
                  const qualified = p.total_points >= 200 ? 'qualified' : 'unqualified';
                  return addr.includes(q) || tasks.includes(q) || qualified.includes(q);
                }).length === 0 && (
                  <p className="text-xs text-white/20 text-center py-4">No wallets match "{walletSearch}"</p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

// ============================================
// Genesis Passports — Admin Viewer
// ============================================
const GenesisAdminViewer = ({ walletAddress, adminToken }) => {
  const [stats, setStats] = useState(null);
  const [passports, setPassports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState({});  // { code: true } - per-code reveal
  const [copied, setCopied] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');  // all | unclaimed | claimed

  const load = useCallback(async () => {
    if (!walletAddress) return;
    setLoading(true);
    try {
      if (adminToken) {
        const statsRes = await fetch(`${API_URL}/api/genesis/stats`, {
          headers: { 'Authorization': `Bearer ${adminToken}` },
        });
        if (statsRes.ok) {
          const s = await statsRes.json();
          setStats({
            total_issued: s.codes_issued || 0,
            total_claimed: s.codes_claimed || 0,
            eligible: s.eligible || 0,
          });
        }

        const listRes = await fetch(`${API_URL}/api/admin/genesis/passports?limit=500`, {
          headers: { 'Authorization': `Bearer ${adminToken}` },
        });
        if (listRes.ok) {
          const data = await listRes.json();
          setPassports(data.passports || []);
        }
      } else {
        // Public fallback — shows safe counters without revealing per-wallet data
        const publicRes = await fetch(`${API_URL}/api/genesis/public-stats`);
        if (publicRes.ok) {
          const p = await publicRes.json();
          setStats({
            total_issued: p.codes_issued || 0,
            total_claimed: p.codes_claimed || 0,
            eligible: p.eligible || 0,
          });
        }
      }
    } catch (e) {
      console.error('Genesis load failed', e);
    } finally {
      setLoading(false);
    }
  }, [walletAddress, adminToken]);

  useEffect(() => { load(); }, [load]);

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 1500);
  };

  const maskWallet = (w) => w ? `${w.slice(0, 6)}…${w.slice(-4)}` : '—';
  const maskCode = (c) => c ? `${c.slice(0, 4)}-${'•'.repeat(4)}-${'•'.repeat(4)}` : '—';

  const filtered = passports.filter((p) => {
    if (filter === 'claimed' && !p.claimed_at) return false;
    if (filter === 'unclaimed' && p.claimed_at) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.code || '').toLowerCase().includes(q)
      || (p.demo_wallet || '').toLowerCase().includes(q)
      || (p.real_wallet || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-5" data-testid="genesis-admin-panel">
      {/* Header */}
      <div className="p-5 rounded-2xl" style={{
        background: 'linear-gradient(135deg, rgba(234,179,8,0.06), rgba(139,92,246,0.04), rgba(0,0,0,0.5))',
        border: '1px solid rgba(234,179,8,0.25)',
        boxShadow: 'inset 0 0 24px rgba(234,179,8,0.04)',
      }}>
        <div className="flex items-center gap-2 mb-1">
          <Crown className="w-4 h-4 text-yellow-300" style={{ filter: 'drop-shadow(0 0 8px #facc15)' }} />
          <h3 className="font-semibold text-sm tracking-wider text-yellow-100/90" style={{ fontFamily: "'Cinzel', serif" }}>
            GENESIS PASSPORTS
          </h3>
          <span className="ml-auto text-[9px] px-2 py-0.5 bg-yellow-500/15 border border-yellow-500/30 text-yellow-300 rounded-full font-bold tracking-wider">
            PRE-LAUNCH LOYALTY
          </span>
        </div>
        <p className="text-[11px] text-white/40 leading-relaxed">
          Secret <code className="text-yellow-300/80 bg-white/[0.04] px-1 py-0.5 rounded">GEN-XXXX-XXXX-XXXX</code> codes issued to demo wallets that played <strong>10+ arcade games</strong>. Claimed at real launch against an authentic wallet.
        </p>
      </div>

      {/* Stats tri-card */}
      {stats && (
        <div className="grid grid-cols-3 gap-3" data-testid="genesis-admin-stats">
          <div className="p-3 bg-white/[0.02] border border-yellow-500/15 rounded-xl">
            <div className="flex items-center gap-1.5 mb-1">
              <Sparkles className="w-3 h-3 text-yellow-400" />
              <p className="text-[10px] text-white/40 uppercase tracking-wider">Inscribed</p>
            </div>
            <p className="text-2xl font-bold text-yellow-300">{stats.total_issued || 0}</p>
          </div>
          <div className="p-3 bg-white/[0.02] border border-purple-500/15 rounded-xl">
            <div className="flex items-center gap-1.5 mb-1">
              <Hash className="w-3 h-3 text-purple-400" />
              <p className="text-[10px] text-white/40 uppercase tracking-wider">Awaiting</p>
            </div>
            <p className="text-2xl font-bold text-purple-300">{(stats.total_issued || 0) - (stats.total_claimed || 0)}</p>
          </div>
          <div className="p-3 bg-white/[0.02] border border-emerald-500/15 rounded-xl">
            <div className="flex items-center gap-1.5 mb-1">
              <CheckCircle className="w-3 h-3 text-emerald-400" />
              <p className="text-[10px] text-white/40 uppercase tracking-wider">Redeemed</p>
            </div>
            <p className="text-2xl font-bold text-emerald-300">{stats.total_claimed || 0}</p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search code / wallet"
            className="w-full pl-9 pr-3 py-2 bg-white/[0.03] border border-white/[0.08] rounded-lg text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-yellow-500/30"
            data-testid="genesis-search-input"
          />
        </div>
        <div className="flex gap-1 p-1 bg-white/[0.02] border border-white/[0.06] rounded-lg">
          {['all', 'unclaimed', 'claimed'].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1 text-[10px] uppercase tracking-wider rounded font-bold transition-colors ${
                filter === f ? 'bg-yellow-500/20 text-yellow-300' : 'text-white/40 hover:text-white/70'
              }`}
              data-testid={`genesis-filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>
        <button onClick={load}
          className="px-3 py-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] rounded-lg text-xs text-white/60 hover:text-white flex items-center gap-1.5"
          data-testid="genesis-refresh-btn"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Passport list */}
      {!adminToken ? (
        <div className="p-6 text-center bg-white/[0.02] border border-yellow-500/10 rounded-xl">
          <Lock className="w-6 h-6 mx-auto mb-2 text-yellow-400/50" />
          <p className="text-xs text-white/50">Sign with admin wallet to reveal full passport codes.</p>
          <p className="text-[10px] text-white/30 mt-1">Stats above are public-safe.</p>
        </div>
      ) : loading ? (
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-yellow-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-center bg-white/[0.02] border border-white/[0.06] rounded-xl">
          <p className="text-xs text-white/40">
            {passports.length === 0 ? 'No passports issued yet. Demo users need to play 10+ games.' : 'No matches.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="genesis-passport-list">
          {filtered.map((p) => {
            const isClaimed = !!p.claimed_at;
            const isRevealed = revealed[p.code];
            return (
              <div key={p.code}
                className={`p-3 rounded-xl border transition-all ${
                  isClaimed
                    ? 'bg-emerald-500/[0.03] border-emerald-500/15'
                    : 'bg-white/[0.02] border-white/[0.06] hover:border-yellow-500/20'
                }`}
                data-testid="genesis-passport-row"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Code */}
                  <button onClick={() => copyCode(p.code)}
                    className="font-mono text-xs text-yellow-300 hover:text-yellow-200 flex items-center gap-1.5 bg-yellow-500/5 px-2 py-1 rounded border border-yellow-500/15"
                    title="Click to copy"
                  >
                    {isRevealed ? p.code : maskCode(p.code)}
                    {copied === p.code
                      ? <ClipboardCheck className="w-3 h-3 text-emerald-400" />
                      : <Copy className="w-3 h-3 text-white/30" />}
                  </button>
                  <button onClick={() => setRevealed({ ...revealed, [p.code]: !isRevealed })}
                    className="text-[10px] text-white/40 hover:text-white/70 flex items-center gap-1"
                  >
                    <Eye className="w-3 h-3" /> {isRevealed ? 'Hide' : 'Reveal'}
                  </button>

                  {/* Status */}
                  <span className={`ml-auto text-[9px] px-2 py-0.5 rounded-full font-bold tracking-wider ${
                    isClaimed ? 'bg-emerald-500/15 text-emerald-300' : 'bg-yellow-500/15 text-yellow-300'
                  }`}>
                    {isClaimed ? 'CLAIMED' : 'UNCLAIMED'}
                  </span>
                </div>

                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[10px]">
                  <div className="flex items-center gap-1.5 text-white/40">
                    <span className="text-white/30">Demo:</span>
                    <code className="text-white/60 font-mono">{maskWallet(p.demo_wallet)}</code>
                  </div>
                  <div className="flex items-center gap-1.5 text-white/40">
                    <span className="text-white/30">Claimed by:</span>
                    <code className={`font-mono ${p.real_wallet ? 'text-emerald-300' : 'text-white/30'}`}>
                      {p.real_wallet ? maskWallet(p.real_wallet) : '—'}
                    </code>
                  </div>
                  <div className="flex items-center gap-1.5 text-white/40">
                    <span className="text-white/30">Issued:</span>
                    <span className="text-white/60">{p.issued_at ? new Date(p.issued_at).toLocaleString() : '—'}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-white/40">
                    <span className="text-white/30">Claimed:</span>
                    <span className="text-white/60">{p.claimed_at ? new Date(p.claimed_at).toLocaleString() : '—'}</span>
                  </div>
                </div>
              </div>
            );
          })}
          <p className="text-[10px] text-white/30 text-center pt-2">
            Showing {filtered.length} of {passports.length} passport{passports.length === 1 ? '' : 's'}
          </p>
        </div>
      )}
    </div>
  );
};



export default function AdminPage() {
  const navigate = useNavigate();
  const { publicKey, connected, signMessage } = useWallet();
  const walletAddress = publicKey?.toBase58();  const [authStatus, setAuthStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(null);
  const [backingUp, setBackingUp] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  const [signing, setSigning] = useState(false);
  const [wipingDemo, setWipingDemo] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState(false);
  const [freshResetting, setFreshResetting] = useState(false);
  const [freshConfirmStep, setFreshConfirmStep] = useState(0);  // 0 = initial, 1 = first confirm, 2 = type-to-confirm
  const [freshTypeInput, setFreshTypeInput] = useState('');
  const [chanWiping, setChanWiping] = useState(false);
  const [chanWipeConfirm, setChanWipeConfirm] = useState(false);
  const [seedingVigil, setSeedingVigil] = useState(false);

  // Check admin status
  useEffect(() => {
    if (!walletAddress) { setAuthStatus(null); setLoading(false); return; }
    fetch(`${API_URL}/api/admin/auth/check?wallet_address=${walletAddress}`)
      .then(r => r.json())
      .then(data => { setAuthStatus(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [walletAddress]);

  const copyWallet = (addr) => {
    navigator.clipboard.writeText(addr);
    setCopied(addr);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleBackup = async () => {
    if (!walletAddress || backingUp) return;
    setBackingUp(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/backup?wallet_address=${walletAddress}`);
      const data = await res.json();
      if (data.success === false) { setResult(data); return; }
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gfg_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setResult({ success: true, message: `Backup downloaded! ${data.summary?.total_profiles || 0} profiles, ${data.summary?.total_arcade_games || 0} games exported.` });
    } catch { setResult({ success: false, message: 'Backup failed' }); }
    finally { setBackingUp(false); }
  };

  const handleWipeDemoData = async () => {
    if (!adminToken || wipingDemo) return;
    if (!wipeConfirm) { setWipeConfirm(true); return; }
    setWipingDemo(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/wipe-demo-data`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
      });
      const data = await res.json();
      if (data.success) {
        const breakdown = Object.entries(data.breakdown || {}).map(([k, v]) => `${k}: ${v}`).join(', ');
        setResult({ success: true, message: `Wiped ${data.total_deleted} demo rows${breakdown ? ' (' + breakdown + ')' : ''}` });
      } else {
        setResult({ success: false, message: data.detail || 'Wipe failed' });
      }
    } catch (e) {
      setResult({ success: false, message: `Wipe failed: ${e.message || 'network error'}` });
    } finally {
      setWipingDemo(false);
      setWipeConfirm(false);
    }
  };

  const handleFreshLaunchReset = async () => {
    if (!adminToken || freshResetting) return;
    if (freshConfirmStep === 0) { setFreshConfirmStep(1); return; }
    if (freshConfirmStep === 1) { setFreshConfirmStep(2); return; }
    // Step 2: require user to type the exact phrase
    if (freshTypeInput !== 'RESET EVERYTHING') {
      setResult({ success: false, message: 'Type "RESET EVERYTHING" exactly to confirm.' });
      return;
    }
    setFreshResetting(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/fresh-launch-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ confirm: 'RESET_EVERYTHING' }),
      });
      const data = await res.json();
      if (data.success) {
        const breakdown = Object.entries(data.breakdown || {}).map(([k, v]) => `${k}: ${v}`).join(', ');
        setResult({ success: true, message: `🔥 Fresh launch complete. ${data.total_deleted} rows wiped. All features disabled. ${breakdown ? '(' + breakdown + ')' : ''}` });
      } else {
        setResult({ success: false, message: data.detail || 'Reset failed' });
      }
    } catch (e) {
      setResult({ success: false, message: `Reset failed: ${e.message || 'network error'}` });
    } finally {
      setFreshResetting(false);
      setFreshConfirmStep(0);
      setFreshTypeInput('');
    }
  };

  const handleChancellorWipe = async () => {
    if (!chanWipeConfirm) { setChanWipeConfirm(true); return; }
    setChanWiping(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/chancellor-wipe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ confirm: 'WIPE_HALLS' }),
      });
      const data = await res.json();
      if (data.success) {
        const breakdown = Object.entries(data.breakdown || {}).map(([k, v]) => `${k.replace('chancellor_', '')}: ${v}`).join(', ');
        setResult({ success: true, message: `Halls wiped. ${data.total_deleted} rows cleared${breakdown ? ' (' + breakdown + ')' : ''}` });
      } else {
        setResult({ success: false, message: data.detail || 'Wipe failed' });
      }
    } catch (e) {
      setResult({ success: false, message: `Wipe failed: ${e.message || 'network error'}` });
    } finally {
      setChanWiping(false);
      setChanWipeConfirm(false);
    }
  };

  const handleSeedVigil = async () => {
    if (!adminToken || seedingVigil) return;
    setSeedingVigil(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/chancellor-seed-vigil`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.success) {
        setResult({ success: true, message: `🌱 Vigil seeded with ${data.total_added} souls. Narration begins within 60s.` });
      } else {
        setResult({ success: false, message: data.detail || 'Seed failed' });
      }
    } catch (e) {
      setResult({ success: false, message: `Seed failed: ${e.message || 'network error'}` });
    } finally {
      setSeedingVigil(false);
    }
  };

  // Admin wallet signature authentication
  const handleAdminSign = async () => {
    if (!walletAddress || !signMessage || signing) return;
    setSigning(true);
    try {
      // Step 1: Get challenge nonce
      const challengeRes = await fetch(`${API_URL}/api/admin/auth/challenge?wallet_address=${walletAddress}`, { method: 'POST' });
      if (!challengeRes.ok) throw new Error('Failed to get challenge');
      const { challenge, nonce } = await challengeRes.json();

      // Step 2: Sign the challenge with wallet
      const messageBytes = new TextEncoder().encode(challenge);
      const signature = await signMessage(messageBytes);
      const signatureB64 = btoa(String.fromCharCode(...signature));

      // Step 3: Verify signature and get JWT
      const verifyRes = await fetch(
        `${API_URL}/api/admin/auth/verify?wallet_address=${walletAddress}&signature=${encodeURIComponent(signatureB64)}&nonce=${nonce}`,
        { method: 'POST' }
      );
      if (!verifyRes.ok) {
        const err = await verifyRes.json();
        throw new Error(err.detail || 'Verification failed');
      }
      const { token } = await verifyRes.json();
      setAdminToken(token);
      setResult({ success: true, message: 'Admin session authenticated. Feature controls unlocked.' });
      setTimeout(() => setResult(null), 3000);
    } catch (e) {
      setResult({ success: false, message: `Auth failed: ${e.message}` });
    }
    finally { setSigning(false); }
  };

  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'features', label: 'Features', icon: Settings },
    { id: 'seasons', label: 'Seasons', icon: Flame },
    { id: 'airdrop', label: 'Airdrop', icon: Gift },
    { id: 'genesis', label: 'Genesis', icon: Crown },
    { id: 'tools', label: 'Tools', icon: Download },
  ];

  // Not connected
  if (!connected) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <div className="fixed inset-0 z-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-red-600/5 rounded-full blur-[100px]" />
        </div>
        <motion.button initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} onClick={() => navigate('/')}
          className="fixed top-6 left-6 z-50 hidden md:flex items-center gap-1.5 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-white/70 hover:text-white transition-all"
          data-testid="admin-back-btn">
          <ChevronLeft className="w-4 h-4" /><span className="text-sm">Home</span>
        </motion.button>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 text-center p-8">
          <Shield className="w-12 h-12 text-red-400/50 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Admin Access Required</h2>
          <p className="text-white/40 text-sm mb-6">Connect an authorized wallet to access the admin panel</p>
          <WalletMultiButton className="!bg-red-500/20 hover:!bg-red-500/30 !border !border-red-500/30 !rounded-xl !h-12 !px-6 mx-auto" />
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-400" />
      </div>
    );
  }

  if (!authStatus?.is_authorized) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <div className="fixed inset-0 z-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-red-600/5 rounded-full blur-[100px]" />
        </div>
        <motion.button initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} onClick={() => navigate('/')}
          className="fixed top-6 left-6 z-50 hidden md:flex items-center gap-1.5 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-white/70 hover:text-white transition-all">
          <ChevronLeft className="w-4 h-4" /><span className="text-sm">Home</span>
        </motion.button>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 text-center p-8">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Unauthorized Wallet</h2>
          <p className="text-white/40 text-sm mb-2">This wallet does not have admin access.</p>
          <p className="text-xs font-mono text-white/20">{walletAddress}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="fixed inset-0 z-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-red-600/5 rounded-full blur-[120px]" />
      </div>

      <motion.button initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} onClick={() => navigate('/')}
        className="fixed top-6 left-6 z-50 hidden md:flex items-center gap-1.5 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-white/70 hover:text-white transition-all"
        data-testid="admin-back-btn">
        <ChevronLeft className="w-4 h-4" /><span className="text-sm">Home</span>
      </motion.button>

      <div className="relative z-10 px-4 sm:px-6 py-20 max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.8 }}
            className="mx-auto w-24 h-[2px] bg-gradient-to-r from-transparent via-red-500 to-transparent mb-3" />
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl md:text-4xl font-black tracking-wider">
            <span style={{
              background: 'linear-gradient(180deg, #ffffff 0%, #fecaca 30%, #ef4444 60%, #dc2626 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 0 20px rgba(239, 68, 68, 0.4))',
            }}>ADMIN PANEL</span>
          </motion.h1>
          <div className="mt-2 flex items-center justify-center gap-2">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-xs text-white/40 font-mono">
              {walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)}
            </span>
          </div>

          {/* Auth Button */}
          {!adminToken ? (
            <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
              onClick={handleAdminSign} disabled={signing}
              className="mt-4 mx-auto flex items-center gap-2 px-5 py-2.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-full text-xs font-semibold text-red-400 transition-all"
              data-testid="admin-sign-btn">
              {signing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
              {signing ? 'Sign in wallet...' : 'Sign to Unlock Controls'}
            </motion.button>
          ) : (
            <div className="mt-3 flex items-center justify-center gap-1.5">
              <CheckCircle className="w-3 h-3 text-emerald-400" />
              <span className="text-[10px] text-emerald-400/70">Session authenticated</span>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-1 scrollbar-hide" data-testid="admin-tabs">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                activeTab === tab.id 
                  ? 'bg-red-500/15 text-red-400 border border-red-500/25' 
                  : 'text-white/30 hover:text-white/60 hover:bg-white/[0.03]'
              }`}
              data-testid={`admin-tab-${tab.id}`}>
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <StatBox label="Season Vault" value="350M" sub="35% of supply" icon={Flame} accent="orange" />
                <StatBox label="Airdrop Pool" value="100M" sub="10% of supply" icon={Gift} />
                <StatBox label="Total Supply" value="1B" sub="$GFG (deflationary)" icon={Coins} />
                <StatBox label="LP Locked" value="300M" sub="30% of supply" icon={Lock} accent="emerald" />
              </div>

              {/* Metrics Dashboard */}
              <MetricsDashboard walletAddress={walletAddress} />

              {/* Token Allocation */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                className="p-5 bg-white/[0.02] border border-white/[0.06] rounded-2xl mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Coins className="w-4 h-4 text-red-400" />
                  <h3 className="font-semibold text-sm">Token Allocation</h3>
                </div>
                <div className="space-y-2.5">
                  {[
                    { label: 'Liquidity Pool', value: '300M', pct: '30%', color: 'bg-emerald-400', width: '30%' },
                    { label: 'Season Vault', value: '350M', pct: '35%', color: 'bg-orange-400', width: '35%' },
                    { label: 'Arcade Economy', value: '150M', pct: '15%', color: 'bg-purple-400', width: '15%' },
                    { label: 'Airdrop', value: '100M', pct: '10%', color: 'bg-red-400', width: '10%' },
                    { label: 'Team (locked)', value: '100M', pct: '10%', color: 'bg-white/30', width: '10%' },
                  ].map(a => (
                    <div key={a.label}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-white/50">{a.label}</span>
                        <span className="text-white/70 font-mono">{a.value} <span className="text-white/30">({a.pct})</span></span>
                      </div>
                      <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                        <div className={`h-full ${a.color} rounded-full`} style={{ width: a.width }} />
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}

          {activeTab === 'features' && (
            <motion.div key="features" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <FeatureAccessControl adminToken={adminToken} walletAddress={walletAddress} />
            </motion.div>
          )}

          {activeTab === 'seasons' && (
            <motion.div key="seasons" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <SeasonControl adminToken={adminToken} />
            </motion.div>
          )}

          {activeTab === 'airdrop' && (
            <motion.div key="airdrop" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="space-y-6">
              <AirdropPhaseControl walletAddress={walletAddress} adminToken={adminToken} />
              <AirdropCodesSection walletAddress={walletAddress} />
            </motion.div>
          )}

          {activeTab === 'genesis' && (
            <motion.div key="genesis" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <GenesisAdminViewer walletAddress={walletAddress} adminToken={adminToken} />
            </motion.div>
          )}

          {activeTab === 'tools' && (
            <motion.div key="tools" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="space-y-6">
              {/* Data Backup */}
              <div className="p-5 bg-white/[0.02] border border-white/[0.06] rounded-2xl" data-testid="backup-section">
                <div className="flex items-center gap-2 mb-3">
                  <Download className="w-4 h-4 text-cyan-400" />
                  <h3 className="font-semibold text-sm">Data Backup</h3>
                </div>
                <p className="text-xs text-white/40 mb-4">Download all player data, game history, and season records as JSON.</p>
                <button onClick={handleBackup} disabled={backingUp}
                  className="w-full py-2.5 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg font-medium text-sm text-cyan-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="backup-download-btn">
                  {backingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4" /> Download Backup</>}
                </button>
              </div>

              {/* Wipe Demo Data */}
              <div className="p-5 bg-white/[0.02] border border-white/[0.06] rounded-2xl" data-testid="wipe-demo-section">
                <div className="flex items-center gap-2 mb-3">
                  <Trash2 className="w-4 h-4 text-orange-400" />
                  <h3 className="font-semibold text-sm">Wipe Demo Data</h3>
                  {!adminToken && <span className="ml-auto text-[8px] px-2 py-0.5 bg-yellow-500/10 text-yellow-400 rounded-full font-bold">SIGN TO UNLOCK</span>}
                </div>
                <p className="text-xs text-white/40 mb-4 leading-relaxed">
                  Deletes all rows across demo-relevant collections where <code className="text-orange-300/80 bg-white/[0.04] px-1 py-0.5 rounded">wallet_address</code> starts with <code className="text-orange-300/80 bg-white/[0.04] px-1 py-0.5 rounded">Demo</code>. Use this right before real launch to clear pre-launch demo traffic from the leaderboard. <strong className="text-orange-300">Irreversible.</strong>
                </p>
                {wipeConfirm && !wipingDemo && (
                  <div className="mb-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg text-xs text-orange-200">
                    Are you sure? Click again to confirm. <button onClick={() => setWipeConfirm(false)} className="ml-2 underline hover:text-white">Cancel</button>
                  </div>
                )}
                <button
                  onClick={handleWipeDemoData}
                  disabled={!adminToken || wipingDemo}
                  className={`w-full py-2.5 border rounded-lg font-medium text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                    wipeConfirm
                      ? 'bg-red-500/25 hover:bg-red-500/40 border-red-500/50 text-red-300'
                      : 'bg-orange-500/15 hover:bg-orange-500/25 border-orange-500/30 text-orange-300'
                  }`}
                  data-testid="wipe-demo-btn"
                >
                  {wipingDemo
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Wiping...</>
                    : wipeConfirm
                      ? <><AlertTriangle className="w-4 h-4" /> Confirm Wipe</>
                      : <><Trash2 className="w-4 h-4" /> Wipe Demo Data</>}
                </button>
              </div>

              {/* Seed the Vigil — opt-in known whales for instant cold-start */}
              <div className="p-5 rounded-2xl" style={{
                background: 'linear-gradient(135deg, rgba(91,229,132,0.05), rgba(0,0,0,0.5))',
                border: '1px solid rgba(91,229,132,0.30)',
              }} data-testid="seed-vigil-section">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4" style={{ color: '#5be584', filter: 'drop-shadow(0 0 6px rgba(91,229,132,0.5))' }} />
                  <h3 className="font-semibold text-sm" style={{ color: '#5be584' }}>Seed the Vigil</h3>
                  {!adminToken && <span className="ml-auto text-[8px] px-2 py-0.5 bg-yellow-500/10 text-yellow-400 rounded-full font-bold">SIGN TO UNLOCK</span>}
                </div>
                <p className="text-xs text-white/50 mb-4 leading-relaxed">
                  Opts in <strong className="text-emerald-300">8 known active Solana wallets</strong> (Anatoly, Ansem, Cupsey, Raydium, Jupiter, Orca, Binance hot, Alameda) so the Chancellor begins narrating real on-chain trades within 60 seconds. Use this <strong className="text-emerald-300">right before announcing</strong> on Discord/X to avoid a cold-start empty feed. Idempotent — safe to click multiple times.
                </p>
                <button
                  onClick={handleSeedVigil}
                  disabled={!adminToken || seedingVigil}
                  className="w-full py-2.5 border rounded-lg font-medium text-sm transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                  style={{
                    background: 'rgba(91,229,132,0.10)',
                    borderColor: 'rgba(91,229,132,0.35)',
                    color: '#5be584',
                    fontFamily: "'Cinzel', serif",
                    letterSpacing: '0.08em',
                  }}
                  data-testid="seed-vigil-btn"
                >
                  {seedingVigil
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Summoning souls…</>
                    : <><Sparkles className="w-4 h-4" /> Seed the Vigil (8 souls)</>}
                </button>
              </div>

              {/* Chancellor Wipe — surgical clear of Halls AI suite test data */}
              <div className="p-5 rounded-2xl" style={{
                background: 'linear-gradient(135deg, rgba(220,200,120,0.05), rgba(0,0,0,0.5))',
                border: '1px solid rgba(220,200,120,0.30)',
              }} data-testid="chancellor-wipe-section">
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="w-4 h-4" style={{ color: '#dcc878', filter: 'drop-shadow(0 0 6px rgba(220,200,120,0.5))' }} />
                  <h3 className="font-semibold text-sm" style={{ color: '#dcc878' }}>Wipe Halls of the Chancellor</h3>
                  {!adminToken && <span className="ml-auto text-[8px] px-2 py-0.5 bg-yellow-500/10 text-yellow-400 rounded-full font-bold">SIGN TO UNLOCK</span>}
                </div>
                <p className="text-xs text-white/50 mb-4 leading-relaxed">
                  Surgical wipe of <strong className="text-amber-200">all 6 Chancellor collections</strong>: dossiers, oaths, watched souls, watch events, coordination events, and codex utterances. The Codex TF-IDF index is rebuilt clean on next query. <strong className="text-amber-200">Player data, seasons, arcade scores untouched.</strong> Use this to clear pre-launch test narrations.
                </p>
                {chanWipeConfirm && !chanWiping && (
                  <div className="mb-3 p-3 rounded-lg text-xs" style={{ background: 'rgba(220,200,120,0.08)', border: '1px solid rgba(220,200,120,0.35)', color: '#e6dcc8' }}>
                    Confirm: every Vigil narration, every oath, every dossier, every codex memory will be erased. Click again to proceed.
                    <button onClick={() => setChanWipeConfirm(false)} className="ml-2 underline hover:text-white">Cancel</button>
                  </div>
                )}
                <button
                  onClick={handleChancellorWipe}
                  disabled={!adminToken || chanWiping}
                  className="w-full py-2.5 border rounded-lg font-medium text-sm transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                  style={{
                    background: chanWipeConfirm ? 'rgba(220,200,120,0.25)' : 'rgba(220,200,120,0.10)',
                    borderColor: chanWipeConfirm ? 'rgba(220,200,120,0.65)' : 'rgba(220,200,120,0.35)',
                    color: '#dcc878',
                    fontFamily: "'Cinzel', serif",
                    letterSpacing: '0.08em',
                  }}
                  data-testid="chancellor-wipe-btn"
                >
                  {chanWiping
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Erasing the Halls…</>
                    : chanWipeConfirm
                      ? <><AlertTriangle className="w-4 h-4" /> Confirm Wipe</>
                      : <><Eye className="w-4 h-4" /> Wipe Halls of the Chancellor</>}
                </button>
              </div>

              {/* 🔥 FRESH LAUNCH RESET — NUCLEAR */}
              <div className="p-5 rounded-2xl" style={{
                background: 'linear-gradient(135deg, rgba(220,30,0,0.08), rgba(0,0,0,0.5))',
                border: '1px solid rgba(220,30,0,0.4)',
                boxShadow: 'inset 0 0 20px rgba(220,30,0,0.05)',
              }} data-testid="fresh-launch-section">
                <div className="flex items-center gap-2 mb-3">
                  <Flame className="w-4 h-4 text-red-500" style={{ filter: 'drop-shadow(0 0 6px #ef4444)' }} />
                  <h3 className="font-semibold text-sm text-red-300">Fresh Launch Reset</h3>
                  <span className="ml-auto text-[8px] px-2 py-0.5 bg-red-500/20 text-red-300 rounded-full font-bold tracking-wider">NUCLEAR</span>
                </div>
                <p className="text-xs text-white/50 mb-4 leading-relaxed">
                  Wipes <strong className="text-red-300">ALL</strong> player data, game history, seasons, burns, missions, streaks, passports — everything. Resets all feature flags to <strong className="text-red-300">disabled</strong>. Use this once right before real public launch on Emergent. <strong className="text-red-300">Irreversible.</strong>
                </p>

                {freshConfirmStep === 1 && (
                  <div className="mb-3 p-3 bg-red-500/10 border border-red-500/40 rounded-lg text-xs text-red-200">
                    <strong>⚠️ Are you sure?</strong> This will delete every player account, every game score, every burn, and every season. Click again to continue.
                    <button
                      onClick={() => { setFreshConfirmStep(0); setFreshTypeInput(''); setResult(null); }}
                      className="ml-2 underline hover:text-white"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {freshConfirmStep === 2 && (
                  <div className="mb-3 p-3 bg-red-500/15 border border-red-500/50 rounded-lg">
                    <p className="text-xs text-red-200 mb-2">
                      <strong>Last chance.</strong> Type <code className="bg-black/40 px-1.5 py-0.5 rounded text-red-300 font-mono">RESET EVERYTHING</code> to confirm:
                    </p>
                    <input
                      type="text"
                      value={freshTypeInput}
                      onChange={(e) => setFreshTypeInput(e.target.value)}
                      placeholder="RESET EVERYTHING"
                      data-testid="fresh-launch-confirm-input"
                      className="w-full px-3 py-2 bg-black/50 border border-red-500/40 rounded text-sm text-red-200 placeholder:text-red-400/30 font-mono outline-none focus:border-red-500/70"
                    />
                    <button
                      onClick={() => { setFreshConfirmStep(0); setFreshTypeInput(''); setResult(null); }}
                      className="mt-2 text-xs underline text-white/50 hover:text-white"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                <button
                  onClick={handleFreshLaunchReset}
                  disabled={!adminToken || freshResetting || (freshConfirmStep === 2 && freshTypeInput !== 'RESET EVERYTHING')}
                  data-testid="fresh-launch-btn"
                  className={`w-full py-2.5 border rounded-lg font-medium text-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                    freshConfirmStep === 0
                      ? 'bg-red-500/10 hover:bg-red-500/20 border-red-500/30 text-red-300'
                      : 'bg-red-600/30 hover:bg-red-600/45 border-red-600/60 text-red-200'
                  }`}
                >
                  {freshResetting
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Wiping everything...</>
                    : freshConfirmStep === 0
                      ? <><Flame className="w-4 h-4" /> Fresh Launch Reset</>
                      : freshConfirmStep === 1
                        ? <><AlertTriangle className="w-4 h-4" /> Continue (1 of 2)</>
                        : <><AlertTriangle className="w-4 h-4" /> Execute Reset</>}
                </button>
              </div>

              {/* Admin Wallet Info */}
              <div className="p-5 bg-white/[0.02] border border-white/[0.06] rounded-2xl">
                <div className="flex items-center gap-2 mb-3">
                  <Wallet className="w-4 h-4 text-white/40" />
                  <h3 className="font-semibold text-sm">Admin Wallet</h3>
                </div>
                <div className="flex items-center justify-between py-2 px-3 bg-white/[0.02] rounded-lg group">
                  <span className="text-xs text-white/40">Connected</span>
                  <button onClick={() => copyWallet(walletAddress)}
                    className="text-xs font-mono text-white/50 hover:text-white flex items-center gap-1.5 transition-colors">
                    {walletAddress}
                    {copied === walletAddress ? <ClipboardCheck className="w-3 h-3 text-red-400" /> : <Copy className="w-3 h-3 text-white/20 group-hover:opacity-100" />}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Result Toast */}
        <AnimatePresence>
          {result && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
              className={`fixed bottom-6 right-6 z-50 max-w-sm p-4 rounded-xl shadow-2xl flex items-center gap-3 ${
                result.success ? 'bg-red-900/90 border border-red-500/30' : 'bg-orange-900/90 border border-orange-500/30'}`}
              data-testid="admin-result-toast">
              {result.success ? <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0" />}
              <p className={`text-sm ${result.success ? 'text-red-300' : 'text-orange-300'}`}>{result.message}</p>
              <button onClick={() => setResult(null)} className="text-white/40 hover:text-white ml-2"><X className="w-4 h-4" /></button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
